import React, { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./TileCard.scss";

import { Card } from "primereact/card";
import { classNames } from "primereact/utils";

import { faGrip, faRemove } from "@fortawesome/pro-regular-svg-icons";

import { FeatureTileDto } from "../../../../../@api";

import { normalizeLanguage, pickLanguageValue } from "../../../../../utils/functions/normalizeLanguage";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useStore } from "../../../../stores/StoreContext";
import { confirmAction } from "../../Functions/ConfirmActionDialogFunction";

interface TileCardProps {
  tile: FeatureTileDto;
  featureIcon: IconProp;
  showAdminControls?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  onEdit?: () => void;
  onRemove?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  isExternalUrl?: boolean;
  customColors?: {
    textColor?: string;
    backgroundColor?: string;
    backgroundHoverColor?: string;
  };
}

const featureKeys = translationKeys.adminArea.featureTiles;

export const TileCard: FunctionComponent<TileCardProps> = props => {
  const { t, i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.language);
  const { userStore } = useStore();

  const title = useMemo(() => pickLanguageValue(props.tile.title, lang), [props.tile, lang]);
  const description = useMemo(() => pickLanguageValue(props.tile.description, lang), [props.tile, lang]);
  const buttonText = useMemo(() => pickLanguageValue(props.tile.buttonText, lang), [props.tile, lang]);
  const tooltip = useMemo(() => pickLanguageValue(props.tile.tooltip, lang), [props.tile, lang]);

  const showAdminControls = props.showAdminControls ?? false;
  const isDisabled = props.disabled ?? false;

  const buttonTooltipInfo = () => tooltip ?? "";

  const cardTitleMemo = useMemo(
    () => (
      <div className="feature-card-header">
        <div className="feature-card-icon">
          <FontAwesomeIcon icon={props.featureIcon} size="xl" />
        </div>

        <div className="feature-card-title">{title}</div>
      </div>
    ),
    [props.featureIcon, userStore.currentLanguage, title],
  );

  const cardContentMemo = useMemo(
    () => <p className="feature-card-content">{description}</p>,
    [userStore.currentLanguage, description],
  );

  const cardFooterMemo = useMemo(
    () => (
      <Button
        label={buttonText}
        id={`.${props.tile.id}-button`}
        className={classNames("feature-card-button icon-button-reverse", { disabled: isDisabled })}
        icon={<FontAwesomeIcon icon={props.featureIcon} />}
        tooltip={buttonTooltipInfo()}
        tooltipOptions={{
          position: "top",
          showOnDisabled: true,
          showDelay: 200,
        }}
      />
    ),
    [isDisabled, buttonText, props.featureIcon, tooltip],
  );

  const handleRemoveClick: React.MouseEventHandler<HTMLElement> = async event => {
    event.stopPropagation();

    const isConfirmed = await confirmAction(t, {
      header: t(featureKeys.actions.delete.name),
      acceptLabel: t(featureKeys.actions.delete.name),
      message: t(featureKeys.actions.delete.confirmationMessage, {
        name: title,
      }),
      acceptClassName: "p-button-confirm",
    });

    if (isConfirmed) {
      props.onRemove?.();
    }
  };

  return (
    <Card
      title={cardTitleMemo}
      className={classNames("tile-card", {
        "external-link": props.isExternalUrl,
        disabled: isDisabled,
      })}
      style={
        props.customColors
          ? ({
              "--tile-text-color": props.customColors.textColor,
              "--tile-bg-color": props.customColors.backgroundColor,
              "--tile-bg-hover-color": props.customColors.backgroundHoverColor,
            } as React.CSSProperties)
          : undefined
      }
      onClick={() => !isDisabled && props.onClick?.()}
    >
      {/* overlay admin controls */}
      {showAdminControls && (
        <div className="admin-tile-controls" onClick={event => event.stopPropagation()}>
          <Button
            className="admin-tile-drag-handle"
            disabled={isDisabled}
            icon={<FontAwesomeIcon icon={faGrip} />}
            tooltip={t(featureKeys.actions.drag.name)}
            tooltipOptions={{
              position: "top",
              showOnDisabled: true,
              showDelay: 200,
            }}
            onClick={event => event.stopPropagation()}
            {...props.dragHandleProps}
          />

          <Button
            className="admin-tile-remove"
            disabled={isDisabled}
            icon={<FontAwesomeIcon icon={faRemove} />}
            tooltip={t(featureKeys.actions.delete.name)}
            tooltipOptions={{
              position: "top",
              showOnDisabled: true,
              showDelay: 200,
            }}
            onClick={handleRemoveClick}
          />
        </div>
      )}

      {cardContentMemo}

      <div className="feature-card-body">{cardFooterMemo}</div>
    </Card>
  );
};
